import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { QrCode, Copy, CheckCircle, RefreshCw, Clock3 } from 'lucide-react';
import { Textarea } from '../../components/ui/textarea';

const formatCurrency = (value) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
const statusLabel = { Pending: 'Aguardando pagamento', Paid: 'Recebido', Cancelled: 'Cancelado', Expired: 'Expirado' };

const PixReceive = () => {
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [charge, setCharge] = useState(null);
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const loadCharges = async () => {
    try {
      const response = await api.get('/me/pix/charges');
      setCharges(response.data || []);
      if (charge) {
        const updated = (response.data || []).find((item) => item.charge_id === charge.charge_id);
        if (updated) setCharge(updated);
      }
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro ao consultar cobranças', description: error.response?.data?.detail || 'Não foi possível atualizar os recebimentos Pix.' });
    }
  };

  useEffect(() => { loadCharges(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await api.post('/me/pix/charge', { amount: Number(amount), description: description.trim() });
      setCharge(response.data);
      await loadCharges();
      toast({ title: 'Cobrança Pix criada', description: 'O saldo só será creditado após a confirmação do pagamento.' });
      setAmount('');
      setDescription('');
    } catch (error) {
      toast({ variant: 'destructive', title: 'Erro', description: error.response?.data?.detail || 'Erro ao gerar cobrança Pix.' });
    } finally { setLoading(false); }
  };

  const handleCopy = async () => {
    if (!charge?.qr_code) return;
    await navigator.clipboard.writeText(charge.qr_code);
    toast({ title: 'Copiado', description: 'Código Pix Sandbox copiado para a área de transferência.' });
  };

  return (
    <div className="space-y-8" data-testid="pix-receive-page">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div><h1 className="text-4xl font-bold tracking-tight">Receber Pix</h1><p className="mt-2 text-muted-foreground">Crie uma cobrança e acompanhe a confirmação do recebimento.</p></div>
        <Button variant="outline" onClick={loadCharges}><RefreshCw className="mr-2 h-4 w-4" />Atualizar</Button>
      </div>

      <Card className="border-amber-300 bg-amber-50/50"><CardContent className="p-4 text-sm"><strong>Sandbox:</strong> criar uma cobrança não altera seu saldo. O crédito só acontece quando o recebimento é confirmado pelo ambiente administrativo, simulando o retorno futuro do banco/PSP.</CardContent></Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card><CardHeader><CardTitle>Nova cobrança Pix</CardTitle><CardDescription>Informe o valor e a identificação do recebimento.</CardDescription></CardHeader><CardContent>
          <form onSubmit={handleGenerate} className="space-y-6">
            <div className="space-y-2"><Label htmlFor="amount">Valor</Label><Input id="amount" type="number" min="0.01" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} required /></div>
            <div className="space-y-2"><Label htmlFor="description">Descrição</Label><Textarea id="description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Ex.: pagamento de serviço" required rows={3} /></div>
            <Button type="submit" className="w-full" disabled={loading}><QrCode className="mr-2 h-5 w-5" />{loading ? 'Gerando...' : 'Gerar cobrança'}</Button>
          </form>
        </CardContent></Card>

        {charge ? <Card><CardHeader><CardTitle className="flex items-center gap-2">{charge.status === 'Paid' ? <CheckCircle className="h-5 w-5" /> : <Clock3 className="h-5 w-5" />}{charge.status === 'Paid' ? 'Pix recebido' : 'Aguardando pagamento'}</CardTitle><CardDescription>TxId {charge.txid}</CardDescription></CardHeader><CardContent className="space-y-5">
          <div className="rounded-lg border p-5 text-center"><p className="text-sm text-muted-foreground">Valor da cobrança</p><p className="mt-1 text-3xl font-bold">{formatCurrency(charge.amount)}</p><Badge className="mt-3" variant={charge.status === 'Paid' ? 'default' : 'secondary'}>{statusLabel[charge.status] || charge.status}</Badge></div>
          <div className="space-y-2"><Label>Pix Copia e Cola · Sandbox</Label><div className="flex gap-2"><Input value={charge.qr_code || ''} readOnly className="font-mono text-xs" /><Button variant="outline" size="icon" onClick={handleCopy}><Copy className="h-4 w-4" /></Button></div></div>
          <p className="text-sm text-muted-foreground">Este código é de simulação e não deve ser utilizado em aplicativos bancários reais.</p>
        </CardContent></Card> : <Card><CardContent className="flex min-h-72 items-center justify-center text-center text-muted-foreground">Crie uma cobrança para visualizar o código de recebimento.</CardContent></Card>}
      </div>

      <Card><CardHeader><CardTitle>Cobranças recentes</CardTitle><CardDescription>O status muda para Recebido somente depois da confirmação financeira.</CardDescription></CardHeader><CardContent>
        <div className="space-y-3">{charges.length === 0 ? <p className="py-6 text-center text-sm text-muted-foreground">Nenhuma cobrança criada.</p> : charges.map((item) => <button key={item.charge_id} type="button" onClick={() => setCharge(item)} className="flex w-full flex-col gap-2 rounded-lg border p-4 text-left hover:bg-muted/40 md:flex-row md:items-center md:justify-between"><div><div className="flex items-center gap-2"><strong>{formatCurrency(item.amount)}</strong><Badge variant={item.status === 'Paid' ? 'default' : 'secondary'}>{statusLabel[item.status] || item.status}</Badge></div><p className="mt-1 text-sm">{item.description}</p><p className="text-xs text-muted-foreground">TxId {item.txid}</p></div><div className="text-xs text-muted-foreground">{new Date(item.created_at).toLocaleString('pt-BR')}</div></button>)}</div>
      </CardContent></Card>
    </div>
  );
};

export default PixReceive;
